import React from 'react';
import Tooltip from 'rc-tooltip';
import InfoIcon from 'components/common/svg/Info/info.svg';
import {
  DateTimeBinning,
  DimensionMode,
  DimensionStatus,
  draggableItemType,
  ExpandListButtonContext,
  FieldEntitiesType,
  UserSalesGroupFieldAlias,
} from 'core/constants/report';
import { DateBinningFormatters } from 'core/constants/formatters';
import { ArrowDown, TickIcon } from 'components/common/svg';
import { components } from 'react-select';
import {
  UserSalesGroupFieldWarning,
  warningFunc,
} from 'components/common/WarningText/warning-text.helper';
import { IDimension } from 'core/models/report-redux';
import { IDimensionMetaField, IFilterConfig, IEntityGroupingProps } from 'core/models/report-response';
import useLocalize from 'components/common/Localization/useLocalize.hook';
import { IDraggableItemIconTooltip } from 'components/common/DraggableItem/draggable-item.interface';
import SingleSelect from 'components/common/Dropdown/single-select-dropdown.component';
import { useSelector } from 'react-redux';

import useEntityGrouping from 'hooks/useEntityGrouping';
import { pendoSelector } from 'redux-v2/global-store/global-store.selector';
import { dropdownMenuSize, mouseEnterDelay } from '../common/constants';
import { PendoClassNames } from '../common/pendoClassNames';
import { isValidArrayAndNotEmpty } from '../common/helpers';

export const getDimensionSelectedCount = (
  maxLimitReached: boolean,
  dimensionsSelected: number,
  maxDimensions: number,
) => (
  <span className={`se-grouping-counter ${maxLimitReached ? 'danger' : ''}`}>
    {`${dimensionsSelected}/${maxDimensions}`}
  </span>
);

export const getInfoIconWithTooltip = (tooltipDescription: string) => (
  <Tooltip
    placement="bottom"
    overlay={(
      <div>
        <span>{tooltipDescription}</span>
      </div>
    )}
    overlayClassName="se-grouping-tooltip"
    mouseEnterDelay={mouseEnterDelay}
  >
    <div className="se-info-icon">
      <InfoIcon width={12} height={12} fill="var(--searchBoxTextColor)" />
    </div>
  </Tooltip>
);

const DropdownIndicator = (props: any) => {
  const { selectProps } = props;
  return (
    <components.DropdownIndicator {...props}>
      {selectProps.dropdownIndicator && (
        <>
          {selectProps.menuIsOpen ? (
            <span className="se-react-select-dropdown-indicator-rotate">
              <ArrowDown width={16} height={16} fill="var(--secondaryColor)" />
            </span>
          ) : (
            <ArrowDown width={19} height={16} fill="var(--secondaryColor)" />
          )}
        </>
      )}
    </components.DropdownIndicator>
  );
};

const ValueContainer = (props: any) => {
  const { children, hasValue } = props;
  let element = null;
  if (hasValue) {
    element = (
      <div className="se-react-select-single-select-cn">
        {children[0]?.props?.data?.abbreviation
          || children[0]?.props?.data?.label}
      </div>
    );
  }
  return (
    <components.ValueContainer {...props}>
      {element}
      {children}
    </components.ValueContainer>
  );
};

const truncateWithEllipsis = (text: string, maxLength: number): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const Option = (props: any) => {
  const { children, isSelected, data } = props;
  const labelText = typeof children === 'string' ? children : data.label || '';
  const labelContent = truncateWithEllipsis(labelText, 27);

  return (
    <components.Option {...props}>
      <div className="se-react-select-option-cn">
        <span className="rs-option-label-cn">{labelContent || '(empty)'}</span>
        <div className="rs-option-example">{data.example}</div>
      </div>
      {isSelected && <TickIcon width={13} height={13} />}
    </components.Option>
  );
};

export const getBinningforDimensions = (
  binningType: string,
  itemType: draggableItemType,
  setBinning: Function,
  index: number,
  selectedPage: String,
) => {
  const dateBinningOptions: any = [];
  let updatedDateTimeBinning = [...DateTimeBinning];
  if (itemType === draggableItemType.ColumnDim) {
    updatedDateTimeBinning = DateTimeBinning.filter(
      (dateType: any) => dateType.label !== 'None',
    );
  }
  if (binningType) {
    dateBinningOptions.push(
      ...updatedDateTimeBinning.filter(
        (item: any) => item.value === binningType,
      ),
    ); // selected item
    dateBinningOptions.push(
      ...updatedDateTimeBinning.filter(
        (item: any) => item.value !== binningType,
      ),
    ); // non selected items
  } else {
    dateBinningOptions.push(...updatedDateTimeBinning);
  }

  return (
    <div
      className={`se-date-binning ${
        binningType !== DateBinningFormatters.NONE ? 'set-binning-border' : ''
      }${PendoClassNames.BinningChange}-${selectedPage}`}
    >
      <SingleSelect
        config={{
          Key: 'date-binning',
          Meta: {
            ClassName: 'se-date-binning',
            Options: dateBinningOptions,
            DropdownIndicator: true,
            Searchable: false,
            Clearable: false,
            AppliedOnClose: false,
            CloseOnSelect: true,
            Components: {
              Option,
              DropdownIndicator,
              ValueContainer,
            },
          },
        }}
        value={binningType}
        onChange={(key, value) => setBinning(index, value)}
      />
    </div>
  );
};

/**
 * Component for selecting entity grouping options
 *
 * Features:
 * - Fetches grouping options on focus if not already loaded
 * - Converts API response to SingleSelect-compatible format
 * - Maintains at least one option (default) if API returns empty
 * - Triggers grouping update on selection change
 *
 * Integration:
 * - Uses Redux for state management
 * - Connects to entity grouping custom hook
 */
export const EntityGrouping = ({
  index,
  dim,
  activeId,
  matchingDimension,
}: IEntityGroupingProps) => {
  const selectedPage = useSelector(pendoSelector);
  const { getAndApplyEntityGrouping, updateEntityGrouping, selectors } = useEntityGrouping(false);
  // Fetch grouping options from Redux store
  const entityGroupOptionsMap = useSelector(selectors.metaFieldSelector);
  const fetchedOptions: IDimensionMetaField[] = entityGroupOptionsMap[dim?.Id] ?? [];
  // Convert store data => SingleSelect-friendly format
  let dropdownOptions = [] as any;
  if (isValidArrayAndNotEmpty(fetchedOptions)) {
    dropdownOptions = fetchedOptions?.filter((option: any) => option.Id !== dim.Props.MetaFieldSelectorID)?.map((opt: any) => ({
      value: opt.Id,
      label: opt.Value,
    }));
  }

  if (dim?.Props?.MetaFieldSelectorID) {
    dropdownOptions.unshift({
      value: dim.Props.MetaFieldSelectorID,
      label: dim.Props.MetaFieldSelectorDisplayName,
    });
  }

  return (
    <div
      className={`se-entity-grouping set-binning-border ${PendoClassNames.EntityGroupingChange}-${selectedPage}`}
    >
      <SingleSelect
        config={{
          Key: 'entity-grouping',
          Meta: {
            ClassName: 'se-entity-grouping',
            Options: dropdownOptions,
            DropdownIndicator: true,
            Searchable: false,
            Clearable: false,
            AppliedOnClose: false,
            CloseOnSelect: true,
            Components: {
              Option,
              DropdownIndicator,
              ValueContainer,
            },
          },
        }}
        menuWidth={`${dropdownMenuSize.small}px`}
        value={dim.Props?.MetaFieldSelectorID}
        onChange={(_key, value) => {
          const opt = fetchedOptions.find((option: IDimensionMetaField) => option.Id === value);
          updateEntityGrouping({ index, opt, activeId });
        }}
        onFocus={() => {
          if (!entityGroupOptionsMap[dim?.Id]) {
            getAndApplyEntityGrouping({
              index,
              data: dim,
              activeId,
              matchingDimension,
            });
          }
        }}
      />
    </div>
  );
};

export const getDimensionTooltip = (
  isUserSalesGroupField: boolean,
  isStringField: boolean,
  partitionColumnFilters?: IFilterConfig[],
  PartitionColumnFilterDateLimit?: number,
  appliedDimensions?: IDimension[],
  activeDimensions?: IDimension[],
  showNotificationOnAdd?: boolean,
  itemType?: draggableItemType,
  hasOthersCol?: boolean,
): IDraggableItemIconTooltip => {
  const showOthersColTooltip = () => hasOthersCol
    && checkIsColGroupingApplied(appliedDimensions, activeDimensions)
    && itemType === draggableItemType.ColumnDim;
  const overlay = isUserSalesGroupField ? (
    <UserSalesGroupFieldWarning />
  ) : (
    warningFunc(
      isStringField,
      partitionColumnFilters,
      PartitionColumnFilterDateLimit,
      showOthersColTooltip(),
      itemType,
    )
  );
  const overlayClassName = isUserSalesGroupField
    ? 'se-sales-group-uesr-field'
    : 'se-string-field-warning-icon-tooltip';
  return {
    overlay,
    overlayClassName,
    defaultVisible: !isUserSalesGroupField && showNotificationOnAdd,
  };
};

export const checkIsColGroupingApplied = (
  appliedDimensions: IDimension[],
  activeDimensions: IDimension[],
) => {
  let isColGroupingApplied = false;
  const appliedColDim = appliedDimensions.find(
    (dim) => dim.Applied !== DimensionStatus.NotApplied
      && dim.DimensionMode === DimensionMode.ColumnGroup,
  );
  const activeColDim = activeDimensions.find(
    (dim) => dim.Applied !== DimensionStatus.NotApplied
      && dim.DimensionMode === DimensionMode.ColumnGroup,
  );
  if (!appliedColDim) return isColGroupingApplied;
  if (appliedColDim.BuilderConfig?.IsDynamicField) {
    isColGroupingApplied = appliedColDim.BuilderConfig?.Alias === activeColDim?.BuilderConfig?.Alias;
  } else {
    isColGroupingApplied = appliedColDim.Name === activeColDim?.Name;
  }
  return isColGroupingApplied;
};

export const checkIsUserSalesGroupingField = (dim: IDimension) => (dim.BuilderConfig?.Entity === FieldEntitiesType.User
  && dim.BuilderConfig?.Alias === UserSalesGroupFieldAlias) || (dim.BuilderConfig?.EntityName && dim.BuilderConfig?.Alias?.startsWith(UserSalesGroupFieldAlias));

export const getDimensionItemIconTooltip = (
  dim: IDimension,
  source: ExpandListButtonContext,
  maxLimitReached: boolean,
  groupingLimit: number,
  isWindowFnDisabled?: boolean,
) => {
  if (dim?.IsMasked) {
    return `${useLocalize('pt.fltrNotAddedPT')}`;
  }
  if (isWindowFnDisabled) {
    return `${useLocalize('psn.windowFnNotInSummary')}`;
  }
  if (maxLimitReached) {
    return `${useLocalize('psn.maxFldReached', [groupingLimit.toString()])}`;
  }
  return null;
};
